import React, { FC, useCallback, useEffect, useRef, useState, RefObject, useMemo } from 'react';
import { createEditor, Descendant, Editor, Range, Text, Transforms, Element as SlateElement, Node } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, Slate, withReact, ReactEditor } from 'slate-react';

import { getBlock, getMarked, toggleMark } from './utils/SlateUtilityFunctions';

import styles from './TextAreaEditor.module.scss';
import withHtmlPaste from './plugins/withHtmlPaste';
import withLists from './plugins/withLists';
import withEmbeds from './plugins/withEmbeds';
import withImagePaste from './plugins/withImagePaste';
import { collectDocIdsFromSlate, fetchImageUrlsBulk } from '../PlateEditor/services/imageUploadService';
import { useEditorSync } from './useEditorSync';
import ToolbarSmall from './Toolbar/ToolbarSmall';
import { htmlToSlate } from './utils/SlateSerialisationUtil';
import clsx from 'clsx';
import MentionInput from '../../modules/Investor/CommentsThread/MentionInput';

interface SlateEditorProps {
    source: string;
    editorInitialData: Descendant[];
    editorContentRef: React.MutableRefObject<Descendant[]>;
    targetRef?: RefObject<HTMLDivElement>;
    assesseeOrgId?: string;
    generating?: boolean;
    onClose?: () => void;
    savingData: boolean;
    editorType: string;

    value?: Descendant[]; // Current editor value (controlled)
    defaultValue?: Descendant[]; // Default value (uncontrolled)
    onChange?: (value: Descendant[]) => void; // Callback when content changes
    name?: string; // Form field name
    wrapperStyles?: any; // Custom styles for the editor wrapper
    editorStyles?: any; // Custom styles for the editor
}

const Element: FC<any> = (props) => {
    return getBlock(props);
};
const Leaf: FC<any> = (props) => {
    let { attributes, children, leaf } = props;
    children = getMarked(leaf, children);
    return <span {...attributes}>{children}</span>;
};

const TextAreaEditor: any = ({
    editorInitialData,
    value,
    placeholder,
    onChange,
    wrapperStyles,
    editorStyles,

    enableMentions = false,
    mentionOptions,
    mentionedUsers,
    setMentionedUsers,
    disableImagePaste = false,
}) => {
    const [editorFocused, setEditorFocused] = useState(false);
    const editor = useMemo(() => {
        const base = withHtmlPaste(withLists(withReact(createEditor())));
        const e = withHistory(withEmbeds(disableImagePaste ? base : withImagePaste(base)));
        const { isInline, normalizeNode } = e;

        e.isInline = (element) => (element as any).type === 'link' || isInline(element);

        // Fix invalid element states and list structure during normalization
        e.normalizeNode = (entry) => {
            const [node, path] = entry;
            try {
                if (SlateElement.isElement(node) && !node.children?.length) {
                    Transforms.insertNodes(e, { text: '' }, { at: [...path, 0] });
                    return;
                }
                // Ensure list-items have block children (not direct text)
                if (SlateElement.isElement(node) && node.type === 'list-item') {
                    for (const [child, childPath] of Node.children(e, path)) {
                        if (Text.isText(child)) {
                            Transforms.wrapNodes(e, { type: 'paragraph', children: [] } as any, { at: childPath });
                            return;
                        }
                    }
                }
                // Ensure lists only contain list-items
                if (SlateElement.isElement(node) && (node.type === 'orderedList' || node.type === 'unorderedList')) {
                    for (const [child, childPath] of Node.children(e, path)) {
                        if (SlateElement.isElement(child) && child.type !== 'list-item') {
                            Transforms.wrapNodes(e, { type: 'list-item', children: [] } as any, { at: childPath });
                            return;
                        }
                        if (Text.isText(child)) {
                            Transforms.wrapNodes(e, { type: 'paragraph', children: [] } as any, { at: childPath });
                            Transforms.wrapNodes(e, { type: 'list-item', children: [] } as any, { at: childPath });
                            return;
                        }
                    }
                    if (node.children.length === 0) {
                        Transforms.removeNodes(e, { at: path });
                        return;
                    }
                }
                normalizeNode(entry);
            } catch (error) {
                console.error('Normalization error:', error);
            }
        };

        return e;
    }, []);

    const initialValue = useMemo((): Descendant[] => {
        if (value && value.length > 0) {
            if (Array.isArray(value)) return value;
            else if (typeof value === 'string') return htmlToSlate(value);
        }
        if (editorInitialData && editorInitialData.length > 0) {
            return editorInitialData;
        }
        return [{ type: 'paragraph', children: [{ text: '' }] } as unknown as Descendant];
    }, [value, editorInitialData]);

    // const isFirstKeyStrokeRef = useRef(false);
    const isControlled = value !== undefined;
    const isUpdatingFromProp = useRef(false);
    const lastProcessedValue = useRef<string>('');

    const [currentMentionWord, setCurrentMentionWord] = useState<string | null>(null);

    useEditorSync(editor, isControlled ? [] : editorInitialData || []);

    // Bulk prefetch presigned URLs for all docId images on load
    // Bulk prefetch presigned URLs for all docId images on load
    useEffect(() => {
        const data = Array.isArray(editorInitialData) ? editorInitialData : [];
        if (!data.length) return;
        const docIds = collectDocIdsFromSlate(data);
        if (docIds.length > 0) fetchImageUrlsBulk(docIds);
    }, [editorInitialData]);

    useEffect(() => {
        synchroniseControlledValueToEditorState();
    }, [isControlled, value, editor]);

    const getCurrentWordAtCursor = useCallback(() => {
        try {
            const { selection } = editor;
            if (!selection || !Range.isCollapsed(selection)) {
                return '';
            }

            const [point] = Range.edges(selection);
            const beforeText = Editor.before(editor, point);
            const beforeRange = beforeText && Editor.range(editor, Editor.start(editor, []), point);
            const textBeforeCursor = beforeRange ? Editor.string(editor, beforeRange) : '';

            const lastAtIndex = textBeforeCursor.lastIndexOf('@');
            if (lastAtIndex !== -1) {
                // Get the candidate from "@" to cursor first
                const mentionCandidate = textBeforeCursor.slice(lastAtIndex);

                // Reject if excessively long
                if (mentionCandidate.length > 64) return '';

                // Allow only typical handle characters after "@"
                if (!/^@[A-Za-z0-9_.-]*$/.test(mentionCandidate)) return '';

                // Check if there are spaces or newlines WITHIN the mention candidate
                if (/[\s\n]/.test(mentionCandidate)) return '';

                // Check if @ is at the start of current paragraph/block (like after pressing Enter)
                const [currentNode] = Editor.node(editor, point);
                const currentText = (currentNode as any)?.text ?? '';
                const textInNodeUpToCursor = currentText.slice(0, point.offset);
                const atIndexInNode = textInNodeUpToCursor.lastIndexOf('@');

                // If the "@" we're considering is at the start of the current text node, it's valid
                if (atIndexInNode === 0) {
                    return mentionCandidate;
                }

                // Otherwise, check normal boundary rules
                const prevChar = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : '';
                const isValidBoundary = lastAtIndex === 0 || /\s/.test(prevChar);

                if (!isValidBoundary) {
                    return ''; // Reject if @ follows non-whitespace (like "hello@world")
                }

                return mentionCandidate;
            }
            return '';
        } catch (error) {
            console.error('Error getting current word:', error);
            return '';
        }
    }, [editor]);

    useEffect(() => {
        if (value && enableMentions) {
            // Get current word based on cursor position, not entire text
            const currentWord = getCurrentWordAtCursor();
            if (currentWord && currentWord.startsWith('@')) {
                setCurrentMentionWord(currentWord);
            } else {
                setCurrentMentionWord(null);
            }
        } else {
            // Ensure stale mention UI is cleared
            setCurrentMentionWord(null);
        }
    }, [value, enableMentions, getCurrentWordAtCursor]);

    const synchroniseControlledValueToEditorState = () => {
        if (!isControlled || isUpdatingFromProp.current) return;

        try {
            let newSlateValue;
            let contentKey;

            // Handle value normalization
            if (Array.isArray(value) && value.length === 0) {
                newSlateValue = [{ type: 'paragraph', children: [{ text: '' }] } as unknown as Descendant];
                contentKey = 'reset';
            } else if (value && value.length > 0) {
                newSlateValue = Array.isArray(value) ? value : htmlToSlate(value);
                contentKey = JSON.stringify(newSlateValue);
            } else {
                newSlateValue = [{ type: 'paragraph', children: [{ text: '' }] } as unknown as Descendant];
                contentKey = 'default';
            }

            const isReset = Array.isArray(value) && value.length === 0;
            // Prevent unnecessary updates (but allow resets)
            if (!isReset && lastProcessedValue.current === contentKey) {
                return;
            }

            const currentContent = JSON.stringify(editor.children);
            const newContent = JSON.stringify(newSlateValue);

            if (currentContent !== newContent) {
                isUpdatingFromProp.current = true;

                // Force a complete reset
                Editor.withoutNormalizing(editor, () => {
                    // Clear everything
                    editor.children = [];
                    editor.selection = null;

                    // Insert new content
                    editor.children.push(...newSlateValue);

                    // Normalize the editor
                    Editor.normalize(editor, { force: true });

                    // Set cursor to start
                    Transforms.select(editor, Editor.start(editor, []));
                });

                lastProcessedValue.current = contentKey;

                setTimeout(() => {
                    isUpdatingFromProp.current = false;
                }, 0);
            }
        } catch (error) {
            console.error('Error syncing editor state:', error);
            isUpdatingFromProp.current = false;
        }
    };

    const getCursorPositionInSlate = (editor) => {
        const { selection } = editor;

        if (!selection || !Range.isCollapsed(selection)) {
            return null; // No cursor or text is selected
        }

        // Get the point where cursor is located
        const [point] = Range.edges(selection);

        // Get text content up to cursor position
        const beforeText = Editor.before(editor, point);
        const beforeRange = beforeText && Editor.range(editor, Editor.start(editor, []), point);
        const textBeforeCursor = beforeRange ? Editor.string(editor, beforeRange) : '';

        return {
            offset: point.offset,
            path: point.path,
            textLength: textBeforeCursor.length,
            textBeforeCursor,
        };
    };

    const handleEditorChange = useCallback(
        (newValue: Descendant[]) => {
            try {
                if (!Array.isArray(newValue) || !newValue.length) return;

                const isAstChange = editor.operations.some((op) => op.type !== 'set_selection');
                if (isAstChange && onChange) onChange(newValue);
            } catch (error) {
                console.error('Editor change error:', error);
                // Don't reset - this prevents data loss. Let the editor maintain its state.
            }
        },
        [editor, onChange],
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (enableMentions) {
                if (event.key === 'Backspace' || event.key === 'Delete') {
                    const { selection } = editor;
                    if (!selection) return;

                    // Handle range selection (including Ctrl+A scenarios)
                    if (!Range.isCollapsed(selection)) {
                        // Get all nodes in the selected range
                        const selectedNodes = Array.from(
                            Editor.nodes(editor, {
                                at: selection,
                                match: (n) => Text.isText(n) && (n as any).mention === true,
                            }),
                        );

                        if (selectedNodes.length > 0) {
                            // If we're deleting mention nodes, clean up the mentioned users state
                            selectedNodes.forEach(([node]) => {
                                if ((node as any).mention && (node as any).mentionUserId) {
                                    // Remove from mentioned users list
                                    setMentionedUsers?.((prev) => prev.filter((userId) => userId !== (node as any).mentionUserId));
                                }
                            });
                        }

                        // Let default behavior handle the deletion
                        return;
                    }

                    // Handle single character deletion for mention nodes
                    if (event.key === 'Backspace') {
                        const [point] = Range.edges(selection);
                        const [leafNode, leafPath] = Editor.leaf(editor, point);

                        // Case 1: Cursor is anywhere within a mention node - delete entire mention
                        if ((leafNode as any).mention) {
                            event.preventDefault();

                            // Remove from mentioned users list
                            if ((leafNode as any).mentionUserId) {
                                setMentionedUsers?.((prev) => prev.filter((userId) => userId !== (leafNode as any).mentionUserId));
                            }

                            // Remove the entire mention node
                            const leafRange = Editor.range(
                                editor,
                                { path: leafPath, offset: 0 },
                                { path: leafPath, offset: leafNode.text.length },
                            );

                            Transforms.select(editor, leafRange);
                            Transforms.delete(editor);

                            return;
                        }

                        // Case 2: Cursor is at the beginning of a non-mention node, check if previous node is a mention
                        if (point.offset === 0) {
                            const beforePoint = Editor.before(editor, point);
                            if (beforePoint) {
                                const [prevLeafNode, prevLeafPath] = Editor.leaf(editor, beforePoint);

                                if ((prevLeafNode as any).mention) {
                                    event.preventDefault();

                                    // Remove from mentioned users list
                                    if ((prevLeafNode as any).mentionUserId) {
                                        setMentionedUsers?.((prev) =>
                                            prev.filter((userId) => userId !== (prevLeafNode as any).mentionUserId),
                                        );
                                    }

                                    // Remove the entire previous mention node
                                    const prevLeafRange = Editor.range(
                                        editor,
                                        { path: prevLeafPath, offset: 0 },
                                        { path: prevLeafPath, offset: prevLeafNode.text.length },
                                    );

                                    Transforms.select(editor, prevLeafRange);
                                    Transforms.delete(editor);

                                    return;
                                }
                            }
                        }

                        // Case 3: Handle deletion within a mention node (for specific character like "@")
                        if (point.offset > 0) {
                            const beforePoint = { ...point, offset: point.offset - 1 };
                            const charRange = Editor.range(editor, beforePoint, point);
                            const charText = Editor.string(editor, charRange);

                            // If we're deleting "@" from a mention node, convert to plain text
                            if ((leafNode as any).mention && charText === '@') {
                                event.preventDefault();

                                // Remove the "@" and convert to plain text
                                const remainingText = leafNode.text.substring(1);
                                const marks = { ...leafNode };

                                // Remove mention-specific properties
                                delete (marks as any).mention;
                                delete (marks as any).mentionUserId;
                                delete (marks as any).mentionUserName;
                                delete (marks as any).text;

                                // Remove from mentioned users list
                                if ((leafNode as any).mentionUserId) {
                                    setMentionedUsers?.((prev) => prev.filter((userId) => userId !== (leafNode as any).mentionUserId));
                                }

                                // Replace the entire mention node
                                const leafRange = Editor.range(
                                    editor,
                                    { path: leafPath, offset: 0 },
                                    { path: leafPath, offset: leafNode.text.length },
                                );

                                Transforms.select(editor, leafRange);
                                Transforms.delete(editor);

                                if (remainingText) {
                                    Transforms.insertText(editor, remainingText);
                                    // Apply remaining formatting
                                    Object.entries(marks).forEach(([key, value]) => {
                                        if (value !== null && value !== undefined) {
                                            Editor.addMark(editor, key, value);
                                        }
                                    });
                                }

                                return;
                            }
                        }
                    }
                }
            }
            // Typically Mac's "Command" key is "metaKey" and Windows' Control is `ctrlKey`.
            // Adjust if you want cross-platform behavior, e.g. using event.ctrlKey instead.
            if (event.metaKey) {
                switch (event.key) {
                    case 'b': {
                        event.preventDefault();
                        toggleMark(editor, 'bold');
                        break;
                    }
                    case 'i': {
                        event.preventDefault();
                        toggleMark(editor, 'italic');
                        break;
                    }
                    case 'u': {
                        event.preventDefault();
                        toggleMark(editor, 'underline');
                        break;
                    }
                    case 's': {
                        event.preventDefault();
                        toggleMark(editor, 'strikethrough');
                        break;
                    }
                    default:
                        break;
                }
            }
        },
        [editor, enableMentions, setMentionedUsers],
    );

    const renderElement = useCallback((props: any) => <Element {...props} />, []);
    const renderLeaf = useCallback((props: any) => {
        const { attributes, children, leaf } = props;
        let style: any = {};
        if (leaf.fontFamily) {
            style.fontFamily = leaf.fontFamily;
        }
        if (leaf.mention) {
            style = {
                ...style,
                color: '#6021B3',
                borderRadius: '4px',
                fontWeight: '500',
            };
        }
        return (
            <span {...attributes} style={style}>
                {getMarked(leaf, children)}
            </span>
        );
    }, []);

    return (
        <div className={clsx(styles.SlateWrapper, { [styles.SlateWrapper_Focused]: editorFocused })}>
            <Slate editor={editor} initialValue={initialValue} onChange={handleEditorChange}>
                <ToolbarSmall />
                <div className={styles.EditorWrapper2} style={wrapperStyles || {}}>
                    <Editable
                        placeholder={placeholder}
                        renderElement={renderElement}
                        renderLeaf={renderLeaf}
                        onKeyDown={handleKeyDown}
                        spellCheck={true}
                        style={editorStyles || {}}
                        onFocus={() => setEditorFocused(true)}
                        onBlur={() => setEditorFocused(false)}
                    />
                </div>
            </Slate>

            {enableMentions && (
                <MentionInput
                    mentionedUsers={mentionedUsers}
                    setMentionedUsers={setMentionedUsers}
                    editor={editor}
                    mentionsActive={!!currentMentionWord}
                    setMentionsActive={setCurrentMentionWord}
                    currentMentionWord={currentMentionWord}
                    placeholder={''}
                    mentionOptions={mentionOptions}
                />
            )}
        </div>
    );
};

export default TextAreaEditor;
